import {
  booleanExpressionTypes,
  datetimeExpressionTypes,
  numericExpressionTypes,
  numericTypePrefixes,
} from '#lit/common/constants/builders/audience_builder.js';
import type {StemNode} from '#lit/lib/builder/audience_builder/nodes/stem.js';
import {TextNode} from '#lit/lib/builder/audience_builder/nodes/text.js';
import type {IClassicBuilderNode} from '#builder';
import type {
  AudienceBuilderField,
  ExpressionValueTuple,
  IArrayExpression,
  IBooleanExpression,
  IDatetimeExpression,
  IFilterExpression,
  INumberExpression,
  IStringExpression,
  ValidArrayExpressionValue,
  ValidExpressionValue,
} from '#lit/types/builders/audience_builder/index.js';
import {
  AudienceBuilderNodeTypes,
  ExpressionOperators,
  ExpressionTypes,
} from '#lit/types/builders/audience_builder/index.js';
import type {DatasetType} from '#lit/types/datasets.js';
import type {Nullable} from '#lit/types/general.js';

export function isBooleanValue(
  value: Nullable<boolean>
): value is Nullable<boolean> {
  return typeof value === 'boolean' || value === null;
}

export function isFilterExpression(
  expression: IFilterExpression
): expression is IFilterExpression {
  const ex = expression as IFilterExpression;
  const [value] = ex.value;

  return (
    typeof ex.field === 'string' &&
    typeof value !== 'undefined' &&
    Object.values(ExpressionTypes).includes(ex.type) &&
    ex.operator !== undefined
  );
}

export function isRelativeOperator(label: ExpressionOperators) {
  if (!label) return false;
  if (
    label === ExpressionOperators.IS_TODAY ||
    label === ExpressionOperators.IS_YESTERDAY ||
    label === ExpressionOperators.IS_TOMORROW
  ) {
    return true;
  }
}
/**
 * Determines whether an object is a string expression.
 */
export function isStringExpression(
  expression: IStringExpression
): expression is IStringExpression {
  const ex = expression as IStringExpression;
  const [value] = ex.value;

  return (
    isFilterExpression(expression) &&
    typeof value === 'string' &&
    ex.type === ExpressionTypes.STRING
  );
}

/**
 * Determines whether an object is a number expression.
 */
export function isNumberExpression(
  expression: INumberExpression
): expression is INumberExpression {
  const ex = expression as INumberExpression;
  const [value] = ex.value;

  return (
    isFilterExpression(expression) &&
    typeof value === 'number' &&
    numericExpressionTypes.includes(ex.type)
  );
}

/**
 * Determines whether an object is a boolean expression.
 */
export function isBooleanExpression(
  expression: IBooleanExpression
): expression is IBooleanExpression {
  const ex = expression as IBooleanExpression;
  const [value] = ex.value;

  return (
    isFilterExpression(expression) &&
    isBooleanValue(value) &&
    booleanExpressionTypes.includes(ex.type)
  );
}

/**
 * Determines whether an object is a datetime expression.
 */
export function isDatetimeExpression(
  expression: IDatetimeExpression
): expression is IDatetimeExpression {
  const ex = expression as IDatetimeExpression;
  const [value] = ex.value;

  return (
    isFilterExpression(expression) &&
    value instanceof Date &&
    datetimeExpressionTypes.includes(ex.type)
  );
}

/**
 * Determines whether an object is a datetime expression.
 */
export function isArrayExpression(
  expression: IArrayExpression
): expression is IArrayExpression {
  const ex = expression as IArrayExpression;
  const [value] = ex.value;

  return (
    isFilterExpression(expression) &&
    Array.isArray(value) &&
    value.every(x => ['string', 'number'].includes(typeof x))
  );
}

/**
 * Returns a null expression value.
 */
export function nullExpressionValue<
  T = ValidExpressionValue,
>(): ExpressionValueTuple<T> {
  return [null];
}

/**
 * Creates a URL to search field values.
 */
export const createFieldValueSearchURL = (
  field: string,
  datasetGroupId: number,
  datasetType: DatasetType
): URL => {
  const url = new URL(
    `/v2/dataset_groups/${datasetGroupId}/field_search?field=${field}&dataset_type=${datasetType}`
  );

  return url;
};

export function isFilter(node: IClassicBuilderNode): boolean {
  if (node.type === AudienceBuilderNodeTypes.FILTER) return true;
  if (node.type === AudienceBuilderNodeTypes.FLEX) return true;
  if (node.type === AudienceBuilderNodeTypes.AUDIENCE) return true;
  if (isStemNode(node)) {
    return node.treatAsFilter;
  }

  return false;
}

export function isOperatable(node: IClassicBuilderNode) {
  return isFilter(node) || isFlex(node);
}

export function isFlex(node: IClassicBuilderNode) {
  return node.type === AudienceBuilderNodeTypes.FLEX;
}

export function isStem(
  node: IClassicBuilderNode | undefined | null
): node is StemNode {
  if (node?.type === AudienceBuilderNodeTypes.STEM) return true;
  if (node?.type === AudienceBuilderNodeTypes.FLEX) return true;
  return false;
}

export function isNumericArrayValue(
  value: ValidArrayExpressionValue | null
): value is number {
  return !isNaN(value as number);
}

export function isDefaultArrayValue(
  value: ValidArrayExpressionValue | null
): value is number {
  return Array.isArray(value as Array<string | number>);
}

/**
 * Predicate for whether or not a given string is an ExpressionType.
 */
export function isExpressionType(type: string): type is ExpressionTypes {
  return Object.values(ExpressionTypes).includes(type as ExpressionTypes);
}

/** Determines wheter an `ExpresionType` is a "string" type. */
export function isStringExpressionType(type: ExpressionTypes): boolean {
  switch (type) {
    case ExpressionTypes.STRING:
      return true;
    default:
      return false;
  }
}

/** Determines wheter an `ExpresionType` is a "numeric" type. */
export function isNumericExpressionType(type: ExpressionTypes): boolean {
  switch (type) {
    case ExpressionTypes.INT:
    case ExpressionTypes.INT_64:
    case ExpressionTypes.INTEGER:
    case ExpressionTypes.INTEGER_64:
    case ExpressionTypes.FLOAT:
    case ExpressionTypes.FLOAT_64:
    case ExpressionTypes.NUMBER:
    case ExpressionTypes.NUMERIC:
      return true;
    default:
      return false;
  }
}

/** Determines wheter an `ExpresionType` is a "boolean" type. */
export function isBooleanExpressionType(type: ExpressionTypes): boolean {
  switch (type) {
    case ExpressionTypes.BOOL:
    case ExpressionTypes.BOOLEAN:
      return true;
    default:
      return false;
  }
}

/** Determines wheter an `ExpresionType` is a "datetime" type. */
export function isDatetimeExpressionType(type: ExpressionTypes): boolean {
  switch (type) {
    case ExpressionTypes.DATE:
    case ExpressionTypes.DATETIME:
    case ExpressionTypes.TIMESTAMP:
      return true;
    default:
      return false;
  }
}

/** Determines wheter an `ExpresionType` is an "array" type. */
export function isArrayExpressionType(type: ExpressionTypes): boolean {
  switch (type) {
    case ExpressionTypes.ARRAY:
      return true;
    default:
      return false;
  }
}

/**
 * Returns a human readable label for a given expression type.
 */
export function fieldTypeLabel(type: ExpressionTypes): string {
  if (isStringExpressionType(type)) return 'string';
  if (isNumericExpressionType(type)) return 'numeric';
  if (isBooleanExpressionType(type)) return 'boolean';
  if (isDatetimeExpressionType(type)) return 'date';
  if (isArrayExpressionType(type)) return 'array';
  return 'unknown';
}

/**
 * Given 2 Expression types, this function says if they are of the same *group*
 * E.g. INT64 and FLOAT are both from *numeric group*, so they are the same
 */
export function compareFieldTypeLabel(
  a?: ExpressionTypes,
  b?: ExpressionTypes
): boolean {
  return !!a && !!b && fieldTypeLabel(a) === fieldTypeLabel(b);
}

/**
 * Mapping function used to standardize types returned from Quervice.
 */
export function filterTypeTransformer(
  field: AudienceBuilderField
): AudienceBuilderField {
  /**
   * Currently, the type transformer only standardizes "NUMERIC" type fields.
   */
  for (const prefix of numericTypePrefixes) {
    if (String(field.type).startsWith(prefix)) {
      field.type = ExpressionTypes.NUMERIC;
      break;
    }
  }

  return field;
}

export function isTextNode(node: IClassicBuilderNode): node is TextNode {
  return node instanceof TextNode;
}

export function isStemNode(node: IClassicBuilderNode): node is StemNode {
  return node.type === AudienceBuilderNodeTypes.STEM && 'treatAsFilter' in node;
}
